/**
 * Episode Transcription Module
 * Handles transcription rendering and speaker management
 */

import { API } from '../core/api.js';
import { showNotification } from '../components/notifications.js';
import { formatTime, escapeHtml } from '../core/utils.js';

/**
 * Render transcription content
 */
export function renderTranscription(episode) {
    const container = document.getElementById('episode-transcription');
    if (!container) return;

    if (episode.transcription_data?.segments_with_speakers?.length > 0) {
        renderDiarizedTranscription(container, episode);
    } else if (episode.transcription) {
        renderRawTranscription(container, episode.transcription);
    } else {
        const div = document.createElement('div');
        div.className = 'no-transcription';
        const p1 = document.createElement('p');
        p1.textContent = 'No transcription available.';
        const p2 = document.createElement('p');
        p2.textContent = 'Use "Fetch Transcription" to retrieve it from the server.';
        div.appendChild(p1);
        div.appendChild(p2);
        container.textContent = '';
        container.appendChild(div);
    }
}

/**
 * Render speaker-diarized transcription
 */
export function renderDiarizedTranscription(container, episode) {
    const segments = episode.transcription_data.segments_with_speakers;
    const speakerNames = episode.speaker_names || {};
    const speakerSegments = groupSegmentsBySpeaker(segments);
    const uniqueSpeakers = getUniqueSpeakers(episode);  // Use shared function for consistent color mapping
    const savedFont = localStorage.getItem('transcript-font') || 'sans';

    container.textContent = '';

    // Add diarization metadata header
    const transcriptionData = episode.transcription_data;
    if (transcriptionData.has_diarization) {
        const metaDiv = document.createElement('div');
        metaDiv.className = 'diarization-meta';
        metaDiv.style.cssText = 'padding: 0.75rem 1rem; margin-bottom: 1rem; background: var(--bg-secondary); border-radius: 8px; font-size: 0.85rem; color: var(--text-secondary); display: flex; gap: 1.5rem; flex-wrap: wrap;';

        const numSpeakers = transcriptionData.num_speakers || uniqueSpeakers.length;
        const backend = transcriptionData.diarization_backend || 'unknown';
        const updatedAt = transcriptionData.diarization_updated_at;

        // Speakers count
        const speakersSpan = document.createElement('span');
        speakersSpan.style.color = 'var(--accent)';
        speakersSpan.textContent = `🎤 ${numSpeakers} speaker${numSpeakers !== 1 ? 's' : ''}`;
        metaDiv.appendChild(speakersSpan);

        // Backend
        const backendSpan = document.createElement('span');
        backendSpan.textContent = 'Backend: ';
        const backendStrong = document.createElement('strong');
        backendStrong.textContent = backend;
        backendSpan.appendChild(backendStrong);
        metaDiv.appendChild(backendSpan);

        // Updated timestamp
        if (updatedAt) {
            const date = new Date(updatedAt);
            const formatted = date.toLocaleString();
            const updatedSpan = document.createElement('span');
            updatedSpan.textContent = `Updated: ${formatted}`;
            metaDiv.appendChild(updatedSpan);
        }

        container.appendChild(metaDiv);
    }

    // Create diarized view
    const diarizedDiv = document.createElement('div');
    diarizedDiv.id = 'transcription-diarized';
    diarizedDiv.className = `transcription-segments transcript-font-${savedFont}`;

    speakerSegments.forEach(seg => {
        const displayName = speakerNames[seg.speaker] || seg.speaker;
        const speakerIndex = uniqueSpeakers.indexOf(seg.speaker) % 8;

        const segmentDiv = document.createElement('div');
        segmentDiv.className = 'transcript-segment';
        segmentDiv.dataset.start = seg.start || 0;
        segmentDiv.dataset.end = seg.end || 999999;
        segmentDiv.dataset.speakerIndex = speakerIndex;

        const headerDiv = document.createElement('div');
        headerDiv.className = 'segment-header';

        const speakerSpan = document.createElement('span');
        speakerSpan.className = 'speaker';
        speakerSpan.dataset.originalSpeaker = seg.speaker;
        speakerSpan.textContent = displayName;
        headerDiv.appendChild(speakerSpan);

        if (seg.start) {
            const timestampSpan = document.createElement('span');
            timestampSpan.className = 'timestamp-inline';
            timestampSpan.title = `Jump to ${formatTime(seg.start)}`;
            timestampSpan.textContent = formatTime(seg.start);
            timestampSpan.onclick = () => seekToTime(seg.start);
            headerDiv.appendChild(timestampSpan);
        }

        const textDiv = document.createElement('div');
        textDiv.className = 'segment-text';
        textDiv.textContent = seg.text;

        segmentDiv.appendChild(headerDiv);
        segmentDiv.appendChild(textDiv);
        diarizedDiv.appendChild(segmentDiv);
    });

    container.appendChild(diarizedDiv);

    // Create raw view (hidden)
    const rawDiv = document.createElement('div');
    rawDiv.id = 'transcription-raw';
    rawDiv.className = `transcription-raw transcript-font-${savedFont}`;
    rawDiv.style.display = 'none';
    rawDiv.textContent = episode.transcription || '';
    container.appendChild(rawDiv);

    setupSegmentHighlighting();
}

/**
 * Render raw transcription (no speaker diarization)
 */
export function renderRawTranscription(container, transcription) {
    container.textContent = '';
    const div = document.createElement('div');
    div.id = 'transcription-raw';
    div.className = 'transcription-raw';
    div.textContent = transcription;
    container.appendChild(div);
}

/**
 * Group segments by consecutive speaker
 */
export function groupSegmentsBySpeaker(segments) {
    const speakerSegments = [];
    let currentSpeaker = null;
    let currentTexts = [];
    let segmentStart = null;
    let lastTimestamp = 0;

    segments.forEach((segment) => {
        if (segment.speaker !== currentSpeaker) {
            if (currentSpeaker !== null && currentTexts.length > 0) {
                speakerSegments.push({
                    speaker: currentSpeaker,
                    text: currentTexts.join(' '),
                    start: segmentStart,
                    end: segment.start || null
                });
            }
            currentSpeaker = segment.speaker;
            currentTexts = [segment.text];
            segmentStart = segment.start || null;
        } else {
            currentTexts.push(segment.text);
        }

        if (segment.end) {
            lastTimestamp = segment.end;
        }
    });

    if (currentSpeaker !== null && currentTexts.length > 0) {
        speakerSegments.push({
            speaker: currentSpeaker,
            text: currentTexts.join(' '),
            start: segmentStart,
            end: lastTimestamp || null
        });
    }

    return speakerSegments;
}

/**
 * Get unique speakers from episode transcription data
 * Returns speakers in order of first appearance (to match transcript colors)
 */
export function getUniqueSpeakers(episode) {
    if (!episode.transcription_data?.segments_with_speakers) return [];

    const speakers = [];
    const seen = new Set();
    episode.transcription_data.segments_with_speakers.forEach(seg => {
        if (seg.speaker && !seen.has(seg.speaker)) {
            seen.add(seg.speaker);
            speakers.push(seg.speaker);
        }
    });

    return speakers;
}

/**
 * Setup event handlers for speaker name inputs
 */
export function setupSpeakerNameInputs(episode) {
    const inputs = document.querySelectorAll('.speaker-name-input');

    inputs.forEach(input => {
        input.addEventListener('blur', () => saveSpeakerNameFromInput(input, episode));
        input.addEventListener('keypress', (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                input.blur();
            }
        });
    });
}

/**
 * Save speaker name from input and update display
 */
async function saveSpeakerNameFromInput(input, episode) {
    const speaker = input.dataset.speaker;
    const name = input.value.trim();

    const speakerNames = { ...(episode.speaker_names || {}) };
    if (name) {
        speakerNames[speaker] = name;
    } else {
        delete speakerNames[speaker];
    }

    episode.speaker_names = speakerNames;
    window.currentEpisodeData.speaker_names = speakerNames;

    try {
        await API.put(`/api/episodes/${episode.id}/speaker-names`, speakerNames);
        updateTranscriptionSpeakerNames(speakerNames);
        showNotification('Speaker name saved', 'success');
    } catch (error) {
        console.error('Error saving speaker name:', error);
        showNotification('Failed to save speaker name', 'error');
    }
}

/**
 * Update speaker names in transcription display
 */
export function updateTranscriptionSpeakerNames(speakerNames) {
    const speakerElements = document.querySelectorAll('.transcript-segment .speaker');

    speakerElements.forEach(el => {
        const originalSpeaker = el.dataset.originalSpeaker || el.textContent;

        if (!el.dataset.originalSpeaker) {
            el.dataset.originalSpeaker = el.textContent;
        }

        const original = el.dataset.originalSpeaker;
        el.textContent = speakerNames[original] || original;
    });
}

/**
 * Setup segment highlighting based on audio playback
 */
export function setupSegmentHighlighting() {
    const audio = document.getElementById('global-audio');
    if (!audio) return;

    let lastHighlightedSegment = null;

    const handleTimeUpdate = () => {
        const currentTime = audio.currentTime;
        const segments = document.querySelectorAll('.transcript-segment');
        let currentSegment = null;

        segments.forEach(seg => {
            const start = parseFloat(seg.dataset.start);
            const end = parseFloat(seg.dataset.end);
            if (currentTime >= start && currentTime < end) {
                currentSegment = seg;
            }
        });

        if (currentSegment !== lastHighlightedSegment) {
            if (lastHighlightedSegment) {
                lastHighlightedSegment.classList.remove('active');
            }

            if (currentSegment) {
                currentSegment.classList.add('active');
                currentSegment.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }

            lastHighlightedSegment = currentSegment;
        }
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
}

/**
 * Scroll transcript to the current playback position
 * Called when returning to an episode that's already playing
 */
export function scrollToCurrentSegment() {
    const audio = document.getElementById('global-audio');
    if (!audio || !audio.currentTime) return;

    const currentTime = audio.currentTime;
    const segments = document.querySelectorAll('.transcript-segment');

    // Remove any existing active class
    segments.forEach(seg => seg.classList.remove('active'));

    // Find and highlight the current segment
    segments.forEach(seg => {
        const start = parseFloat(seg.dataset.start);
        const end = parseFloat(seg.dataset.end);
        if (currentTime >= start && currentTime < end) {
            seg.classList.add('active');
            // Use 'center' for more visibility when returning to episode
            seg.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    });
}

/**
 * Change transcript font family
 */
export function changeTranscriptFont(fontType) {
    const validFonts = ['sans', 'serif', 'mono'];
    if (!validFonts.includes(fontType)) return;

    localStorage.setItem('transcript-font', fontType);

    const diarized = document.getElementById('transcription-diarized');
    const raw = document.getElementById('transcription-raw');

    [diarized, raw].forEach(el => {
        if (el) {
            el.classList.remove('transcript-font-sans', 'transcript-font-serif', 'transcript-font-mono');
            el.classList.add(`transcript-font-${fontType}`);
        }
    });
}

/**
 * Toggle between diarized and raw transcription format
 */
export function toggleTranscriptionFormat() {
    const diarized = document.getElementById('transcription-diarized');
    const raw = document.getElementById('transcription-raw');
    const btn = document.getElementById('format-toggle-btn');

    if (!diarized || !raw) return;

    if (diarized.style.display === 'none') {
        diarized.style.display = 'block';
        raw.style.display = 'none';
        if (btn) btn.textContent = 'Raw';
    } else {
        diarized.style.display = 'none';
        raw.style.display = 'block';
        if (btn) btn.textContent = 'Speakers';
    }
}

/**
 * Render a translated transcription (segments or full text mode)
 */
export function renderTranslatedTranscription(container, translation, episode) {
    container.textContent = '';
    const savedFont = localStorage.getItem('transcript-font') || 'sans';

    // Translation banner
    const banner = document.createElement('div');
    banner.className = 'translation-banner';
    const bannerText = document.createElement('span');
    const modelShort = translation.model ? translation.model.split('/').pop() : '';
    const modelSuffix = modelShort && modelShort !== 'default' ? ` · ${modelShort}` : '';
    bannerText.textContent = `Translated to ${translation.language_name || translation.language}${modelSuffix}`;
    banner.appendChild(bannerText);
    const showOrigBtn = document.createElement('button');
    showOrigBtn.textContent = 'Show Original';
    showOrigBtn.onclick = () => window.toggleTranslationView();
    banner.appendChild(showOrigBtn);
    container.appendChild(banner);

    if (translation.mode === 'segments' && translation.segments && translation.segments.length > 0) {
        const speakerNames = episode.speaker_names || {};
        const uniqueSpeakers = getUniqueSpeakers(episode);

        const segmentsDiv = document.createElement('div');
        segmentsDiv.id = 'transcription-translated';
        segmentsDiv.className = `transcription-segments transcript-font-${savedFont}`;

        translation.segments.forEach(seg => {
            const displayName = speakerNames[seg.speaker] || seg.speaker;
            const speakerIndex = uniqueSpeakers.indexOf(seg.speaker) % 8;

            const segmentDiv = document.createElement('div');
            segmentDiv.className = 'transcript-segment';
            segmentDiv.dataset.start = seg.start || 0;
            segmentDiv.dataset.end = seg.end || 999999;
            segmentDiv.dataset.speakerIndex = speakerIndex >= 0 ? speakerIndex : 0;

            const headerDiv = document.createElement('div');
            headerDiv.className = 'segment-header';

            const speakerSpan = document.createElement('span');
            speakerSpan.className = 'speaker';
            speakerSpan.textContent = displayName;
            headerDiv.appendChild(speakerSpan);

            if (seg.start != null) {
                const timestampSpan = document.createElement('span');
                timestampSpan.className = 'timestamp-inline';
                timestampSpan.title = `Jump to ${formatTime(seg.start)}`;
                timestampSpan.textContent = formatTime(seg.start);
                timestampSpan.onclick = () => seekToTime(seg.start);
                headerDiv.appendChild(timestampSpan);
            }

            const textDiv = document.createElement('div');
            textDiv.className = 'segment-text';
            textDiv.textContent = seg.text;

            segmentDiv.appendChild(headerDiv);
            segmentDiv.appendChild(textDiv);
            segmentsDiv.appendChild(segmentDiv);
        });

        container.appendChild(segmentsDiv);
        setupSegmentHighlighting();
    } else {
        // Full text mode
        const textDiv = document.createElement('div');
        textDiv.id = 'transcription-translated';
        textDiv.className = `transcription-raw transcript-font-${savedFont}`;
        textDiv.textContent = translation.text || '';
        container.appendChild(textDiv);
    }
}

/**
 * Seek to time in global audio player
 */
export function seekToTime(seconds) {
    const audio = document.getElementById('global-audio');
    if (audio && seconds !== null && seconds !== undefined) {
        audio.currentTime = seconds;
        if (audio.paused) {
            audio.play();
        }
    }
}
